import React, { useEffect, useState } from 'react';
import { useStateSelector } from '../../store/selectors';
import Rodal from '../Rodal/Rodal';
import { ModalBody, ModalHeader } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { GetBuyer } from '../../slices/marketplace/buyer/thunks/get-buyer.thunk';
import { GetOwnBuyerSellerPreview } from '../../slices/marketplace/buyer/thunks/get-own-buyer-seller-preview.thunk';
import './BuyerProfilePreviewPopup.scss';
import Loader from 'react-loaders';
import { ProfilePreview } from '../../containers/Admin/BuyerMarketplace/BuyerProfile/ProfilePreview/ProfilePreview';
import { LoiForm } from '../../slices/marketplace/loi-form/loi-form';
import { USER_ROLES } from '../../utils/constants';
import ReactSwitch from 'react-switch';

interface BuyerProfilePreviewPopupProps {
    buyerId?: number;
    close(): void;
    showLoiTabs?: boolean;
    loiFormOverride?: LoiForm | null;
    // #931: show the caller's OWN profile exactly as a seller sees it — fetched with the
    // server-side viewAs scoping into a dedicated state slot, so the profile edit tabs
    // rendered behind the popup keep their data.
    sellerPreviewMode?: boolean;
    // Rendered only when the buyer actually has private uploads — toggles between the
    // pre-NDA seller view and the "seller with executed Data NDA" variant.
    showDataNdaSwitcher?: boolean;
}

const BuyerProfilePreviewPopup = (props: BuyerProfilePreviewPopupProps) => {
    const isLoading = useStateSelector((s) =>
        props.sellerPreviewMode
            ? s.buyersState.isSellerPreviewLoading
            : s.buyersState.isLoading
    );
    const sellerPreviewBuyer = useStateSelector(
        (s) => s.buyersState.sellerPreviewBuyer
    );
    const isAdmin = useStateSelector(
        (s) => s.userSummary.summary?.roleId === USER_ROLES.ADMIN
    );
    const showLoiTabs =
        !props.sellerPreviewMode && (props.showLoiTabs || isAdmin);

    const [withDataNda, setWithDataNda] = useState(false);

    const dispatch = useDispatch() as any;
    useEffect(() => {
        fetchProfile();
    }, [withDataNda]);

    const fetchProfile = async () => {
        if (props.sellerPreviewMode) {
            await dispatch(GetOwnBuyerSellerPreview({ withDataNda }));
            return;
        }
        const includeLoiForm = showLoiTabs && props.loiFormOverride === undefined;
        await dispatch(GetBuyer({ buyerId: props.buyerId, includeLoiForm }));
    };

    return (
        <div className="buyer-profile-preview-popup">
            <Rodal
                visible={true}
                onClose={() => {
                    props.close();
                }}
                width={1200}
                animation={'fade'}
                showMask={true}
                centered>
                <ModalHeader className="modal-title">
                    {'Buyer Profile'}
                </ModalHeader>
                <ModalBody>
                    {props.sellerPreviewMode && props.showDataNdaSwitcher && (
                        <div className="seller-preview-nda-switch">
                            <strong>Seller with executed Data NDA</strong>
                            <ReactSwitch
                                offColor="#d92550"
                                checked={withDataNda}
                                onChange={() => setWithDataNda(!withDataNda)}
                                height={22}
                            />
                        </div>
                    )}
                    {isLoading ? (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '200px',
                            }}>
                            <Loader
                                active={isLoading}
                                type="ball-scale-multiple"
                            />
                        </div>
                    ) : (
                        <ProfilePreview
                            isReadonly={true}
                            showLoiTabs={showLoiTabs}
                            loiFormOverride={props.loiFormOverride}
                            buyerOverride={
                                props.sellerPreviewMode
                                    ? sellerPreviewBuyer
                                    : undefined
                            }
                        />
                    )}
                </ModalBody>
            </Rodal>
        </div>
    );
};

export default BuyerProfilePreviewPopup;
